import type { ReactNode, Ref } from 'react';
import React from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';

import { OfferLabel } from 'src/components/atoms/OfferLabel/OfferLabel';
import { MadeToOrder } from 'src/components/molecules/MadeToOrder/MadeToOrder';
import { PartnerMiniModule } from 'src/components/molecules/PartnerMiniModule/PartnerMiniModule';
import { ProductFeaturesWithTracking } from 'src/components/molecules/ProductFeatures/components/ProductFeaturesWithTracking';
import { ProductPriceAndRatingWithTracking } from 'src/components/molecules/ProductPriceAndRating/ProductPriceAndRatingWithTracking';
import type { PDPViewModel } from './types';
import * as styles from './PDPLayout.styles';

export interface PDPLayoutViewProps {
  /** Rendered above the grid in a max-width container */
  breadcrumbs?: ReactNode;
  /** Ref to the CTA grid cell, for drawers rendered outside the view. Only attaches when ctaTrigger is provided. */
  ctaColumnRef?: Ref<HTMLDivElement>;
  /** CTA trigger area (delivery summary, options trigger, basket CTA). Not an inline options section. */
  ctaTrigger?: ReactNode;
  description?: ReactNode;
  /** Wider column, spans rows alongside the narrow-column content on desktop */
  images?: ReactNode;
  linkCarousel?: ReactNode;
  membershipBanner?: ReactNode;
  /** Full-width section with tinted background */
  partnerModules?: ReactNode;
  publicAttributes?: ReactNode;
  recommendedProducts?: ReactNode;
  /** Slot content owns the reviews fragment id and its internal layout */
  reviews?: ReactNode;
  /** Rendered last; caller provides sticky wrapper and trigger wiring */
  stickyCta?: ReactNode;
  trustCarousel?: ReactNode;
  viewModel: PDPViewModel;
}

const Section = ({ children }: { children: ReactNode }) => (
  <div css={[styles.maxWidthContainer, styles.largeSpacingBottom]}>{children}</div>
);

export const PDPLayoutView = ({
  breadcrumbs,
  ctaColumnRef,
  ctaTrigger,
  description,
  images,
  linkCarousel,
  membershipBanner,
  partnerModules,
  publicAttributes,
  recommendedProducts,
  reviews,
  stickyCta,
  trustCarousel,
  viewModel,
}: PDPLayoutViewProps) => (
  <>
    <div css={visuallyHidden} role="status">
      {viewModel.pageLiveRegionText}
    </div>
    <div css={styles.wrapper}>
      {breadcrumbs && (
        <div css={[styles.maxWidthContainer, styles.breadcrumbsContainer]}>{breadcrumbs}</div>
      )}
      <div css={[styles.maxWidthContainer, styles.grid, styles.largeSpacingBottom]}>
        <h1 css={[styles.narrowColumn, styles.productTitle]}>{viewModel.title}</h1>
        <div css={styles.narrowColumn}>
          <PartnerMiniModule
            {...viewModel.partner}
            partnerRating={viewModel.partnerReviews.rating}
            partnerReviewCount={viewModel.partnerReviews.count}
          />
        </div>
        {images && <div css={[styles.widerColumn, styles.spanAcrossRows]}>{images}</div>}
        <div css={styles.narrowColumn}>
          <ProductPriceAndRatingWithTracking
            averageRating={viewModel.productReviews.rating ?? undefined}
            badge={viewModel.badge}
            numberOfReviews={viewModel.productReviews.count}
            preSalePrices={viewModel.price.preSalePrices}
            price={viewModel.price.price}
            priceIsMinimum={viewModel.price.priceIsMinimum}
            reviewsLinkHref={viewModel.productReviews.href}
            salePercentage={viewModel.price.salePercentage}
            salePercentageInRed={viewModel.price.salePercentageInRed}
          />
          {viewModel.offerLabel && <OfferLabel {...viewModel.offerLabel} />}
          {viewModel.showMadeToOrder && <MadeToOrder />}
        </div>
        {viewModel.productFeatures.length > 0 && (
          <div css={styles.narrowColumn}>
            <ProductFeaturesWithTracking
              currency={viewModel.price.price.currency}
              minimumOptionsCost={viewModel.price.minimumOptionsCost}
              productFeatures={viewModel.productFeatures}
            />
          </div>
        )}
        {ctaTrigger && (
          <div css={styles.narrowColumn} ref={ctaColumnRef}>
            {ctaTrigger}
          </div>
        )}
      </div>
      {description && <Section>{description}</Section>}
      {reviews && <Section>{reviews}</Section>}
      {recommendedProducts && <Section>{recommendedProducts}</Section>}
      {trustCarousel && <Section>{trustCarousel}</Section>}
      {partnerModules && (
        <div css={styles.partnerModules}>
          <Section>{partnerModules}</Section>
        </div>
      )}
      {membershipBanner && <Section>{membershipBanner}</Section>}
      {publicAttributes && <Section>{publicAttributes}</Section>}
      {linkCarousel && <Section>{linkCarousel}</Section>}
      {stickyCta}
    </div>
  </>
);
